/*
 * SendForecastEmailDialog — email the per-owner forecast for a chosen period.
 *
 * Mirrors the UX of Send Leads Summary + Send Project Update:
 *   • Recipient chips (dedupe + e-mail regex)
 *   • Period + deal-type selector (new business / renewals)
 *   • Expected MRR input (total) — manual judgement that doesn't live in Mongo
 *   • Per-owner Expected MRR inputs — populated from the preview table
 *   • Free-form Notes textarea
 *   • Preview table (same aggregation as the Forecast tab)
 *   • One-click send via Microsoft Graph (`salesadmin@xentra.co.uk`)
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mail, Send, X, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PERIOD_OPTIONS = [
  { v: "current_month",   l: "This month" },
  { v: "next_month",      l: "Next month" },
  { v: "last_month",      l: "Last month" },
  { v: "current_quarter", l: "This quarter (FY)" },
  { v: "next_quarter",    l: "Next quarter (FY)" },
  { v: "last_quarter",    l: "Last quarter (FY)" },
];

export default function SendForecastEmailDialog({
  trigger,
  defaultPeriod = "current_month",
  defaultDealType = "new",
}) {
  const [open, setOpen] = useState(false);
  const [recipientInput, setRecipientInput] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [period, setPeriod] = useState(defaultPeriod);
  const [dealType, setDealType] = useState(defaultDealType);
  const [subject, setSubject] = useState("");
  const [notes, setNotes] = useState("");
  const [commitTotal, setCommitTotal] = useState("");
  const [expectedTotal, setExpectedTotal] = useState("");
  // owner-name -> { commit: string, expected: string } — strings so empty
  // inputs don't flicker between 0 and "". Converted on send.
  const [perOwner, setPerOwner] = useState({});
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Fetch the forecast preview — same aggregation the Forecast tab uses
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(
          `/dashboard/forecast/by-owner?period=${period}&deal_type=${dealType}`,
        );
        if (cancelled) return;
        setPreview(r.data);
      } catch (e) {
        if (!cancelled) {
          setPreview(null);
          toast.error(e?.response?.data?.detail || "Could not load forecast preview");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, period, dealType]);

  // Reset form on close so re-opening starts fresh
  useEffect(() => {
    if (!open) {
      setRecipientInput(""); setRecipients([]); setNotes("");
      setCommitTotal(""); setExpectedTotal(""); setPerOwner({}); setSubject("");
      setPreview(null);
    }
  }, [open]);

  const addRecipient = (raw) => {
    const e = (raw || "").trim().replace(/[,;]$/, "");
    if (!e) return;
    if (!EMAIL_RE.test(e)) {
      toast.error(`"${e}" doesn't look like an email`);
      return;
    }
    const lower = e.toLowerCase();
    if (recipients.some((x) => x.toLowerCase() === lower)) return;
    setRecipients([...recipients, e]);
    setRecipientInput("");
  };

  const onRecipientKey = (ev) => {
    if (ev.key === "Enter" || ev.key === "," || ev.key === ";") {
      ev.preventDefault();
      addRecipient(recipientInput);
    } else if (ev.key === "Backspace" && !recipientInput && recipients.length) {
      setRecipients(recipients.slice(0, -1));
    }
  };

  const totals = preview?.totals || {};
  const owners = preview?.owners || [];

  // Per-owner value helpers — safe for undefined owner entries
  const getField = (owner, key) => (perOwner[owner]?.[key] ?? "");
  const setField = (owner, key, value) => setPerOwner({
    ...perOwner,
    [owner]: { ...(perOwner[owner] || {}), [key]: value },
  });

  // Auto-sum owner values into the header totals when the top-level box
  // is blank. If the user typed a top-level number, keep theirs.
  const _sumOwner = (key) => Object.values(perOwner)
    .map((v) => parseFloat(v?.[key]))
    .filter((n) => !Number.isNaN(n))
    .reduce((a, b) => a + b, 0);

  const commitTotalComputed = useMemo(() => {
    if (commitTotal !== "") return commitTotal;
    const sum = _sumOwner("commit");
    return sum ? sum.toFixed(2) : "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perOwner, commitTotal]);

  const expectedTotalComputed = useMemo(() => {
    if (expectedTotal !== "") return expectedTotal;
    const sum = _sumOwner("expected");
    return sum ? sum.toFixed(2) : "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perOwner, expectedTotal]);

  const send = async () => {
    if (!recipients.length) {
      toast.error("Add at least one recipient");
      return;
    }
    setSending(true);
    try {
      const expected_mrr_by_owner = Object.entries(perOwner)
        .map(([owner, v]) => ({
          owner,
          expected_mrr: v?.expected !== undefined && v.expected !== "" ? parseFloat(v.expected) : null,
          commit_mrr: v?.commit !== undefined && v.commit !== "" ? parseFloat(v.commit) : null,
        }))
        .filter((e) => !Number.isNaN(e.expected_mrr) || !Number.isNaN(e.commit_mrr))
        .map((e) => ({
          owner: e.owner,
          expected_mrr: Number.isNaN(e.expected_mrr) ? null : e.expected_mrr,
          commit_mrr: Number.isNaN(e.commit_mrr) ? null : e.commit_mrr,
        }));
      await api.post("/dashboard/forecast/send-email", {
        recipients,
        period,
        deal_type: dealType,
        subject: subject.trim() || null,
        notes: notes.trim() || null,
        commit_mrr_total: commitTotalComputed !== ""
          ? parseFloat(commitTotalComputed) : null,
        expected_mrr_total: expectedTotalComputed !== ""
          ? parseFloat(expectedTotalComputed) : null,
        expected_mrr_by_owner,
      });
      toast.success(`Forecast emailed to ${recipients.length} recipient${recipients.length === 1 ? "" : "s"}`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send forecast email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            data-testid="send-forecast-email-trigger"
          >
            <Mail className="h-4 w-4" />
            Email forecast
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="send-forecast-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-blue-600" />
            Email forecast
          </DialogTitle>
          <DialogDescription>
            Sends the current forecast — Total MRR, Weighted MRR and your manually-entered Expected MRR —
            with a per-owner breakdown and optional notes. Sent from{" "}
            <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">salesadmin@xentra.co.uk</span>{" "}
            via Microsoft 365.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Recipients */}
          <div className="space-y-1.5">
            <Label htmlFor="forecast-email-recipients">Recipients</Label>
            <div className="min-h-[42px] px-2 py-1.5 border border-slate-200 rounded-md flex flex-wrap gap-1.5 items-center focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition">
              {recipients.map((r) => (
                <span
                  key={r}
                  className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 text-xs font-medium rounded px-2 py-0.5"
                  data-testid={`forecast-email-chip-${r}`}
                >
                  {r}
                  <button
                    type="button"
                    onClick={() => setRecipients(recipients.filter((x) => x !== r))}
                    className="hover:text-blue-900"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input
                id="forecast-email-recipients"
                data-testid="forecast-email-recipient-input"
                value={recipientInput}
                onChange={(e) => setRecipientInput(e.target.value)}
                onKeyDown={onRecipientKey}
                onBlur={() => addRecipient(recipientInput)}
                className="flex-1 min-w-[160px] bg-transparent text-sm outline-none"
                placeholder={recipients.length ? "" : "Enter email, press Enter"}
              />
            </div>
          </div>

          {/* Period + deal type */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Period</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger data-testid="forecast-email-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map((o) => (
                    <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Deal type</Label>
              <Select value={dealType} onValueChange={setDealType}>
                <SelectTrigger data-testid="forecast-email-deal-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New business</SelectItem>
                  <SelectItem value="renewals">Renewals</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Subject override */}
          <div className="space-y-1.5">
            <Label htmlFor="forecast-email-subject">Subject (optional)</Label>
            <Input
              id="forecast-email-subject"
              data-testid="forecast-email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={preview ? `Xentra Forecast — ${preview.label}` : "Xentra Forecast — …"}
            />
          </div>

          {/* Summary KPIs — Total / Weighted / Commit / Expected */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] font-semibold tracking-wider uppercase text-slate-500">Total MRR</div>
              <div className="text-xl font-heading text-slate-900 font-bold mt-1" data-testid="forecast-email-total-mrr">
                {fmtMoney(totals.mrr || 0)}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">{totals.deals_count || 0} deals</div>
            </div>
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
              <div className="text-[11px] font-semibold tracking-wider uppercase text-blue-700">Weighted MRR</div>
              <div className="text-xl font-heading text-blue-900 font-bold mt-1" data-testid="forecast-email-weighted-mrr">
                {fmtMoney(totals.weighted || 0)}
              </div>
              <div className="text-[11px] text-blue-700/70 mt-0.5">MRR × probability</div>
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <Label htmlFor="forecast-email-commit-total" className="text-[11px] font-semibold tracking-wider uppercase text-amber-700">
                Commit MRR
              </Label>
              <Input
                id="forecast-email-commit-total"
                data-testid="forecast-email-commit-total"
                type="number" step="0.01" min="0"
                value={commitTotal}
                onChange={(e) => setCommitTotal(e.target.value)}
                placeholder={commitTotalComputed || "Must land"}
                className="h-8 mt-1 bg-white border-amber-300 focus:border-amber-500 focus:ring-amber-500/30 text-lg font-heading text-amber-900 font-bold"
              />
              <div className="text-[11px] text-amber-700/70 mt-0.5">
                {commitTotal === "" && _sumOwner("commit") > 0
                  ? "Auto-summed from owners"
                  : "Deals we WILL close"}
              </div>
            </div>
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <Label htmlFor="forecast-email-expected-total" className="text-[11px] font-semibold tracking-wider uppercase text-emerald-700">
                Expected MRR
              </Label>
              <Input
                id="forecast-email-expected-total"
                data-testid="forecast-email-expected-total"
                type="number" step="0.01" min="0"
                value={expectedTotal}
                onChange={(e) => setExpectedTotal(e.target.value)}
                placeholder={expectedTotalComputed || "Realistic"}
                className="h-8 mt-1 bg-white border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500/30 text-lg font-heading text-emerald-900 font-bold"
              />
              <div className="text-[11px] text-emerald-700/70 mt-0.5">
                {expectedTotal === "" && _sumOwner("expected") > 0
                  ? "Auto-summed from owners"
                  : "Realistic landing"}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="forecast-email-notes">Notes</Label>
            <Textarea
              id="forecast-email-notes"
              data-testid="forecast-email-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Context for the exec team — risks, big-wins we're chasing, slippage from last month, etc."
              rows={4}
            />
          </div>

          {/* Per-owner table */}
          <div className="space-y-1.5">
            <Label>Per-owner breakdown{preview ? ` · ${preview.label}` : ""}</Label>
            {loading && <div className="text-xs text-slate-500">Loading forecast…</div>}
            {!loading && owners.length === 0 && (
              <div className="text-xs text-slate-500 italic p-3 bg-slate-50 rounded border border-slate-200">
                No open deals forecasting for this period.
              </div>
            )}
            {!loading && owners.length > 0 && (
              <div className="rounded-md border border-slate-200 overflow-hidden">
                <table className="w-full text-sm" data-testid="forecast-email-owner-table">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
                    <tr>
                      <th className="px-2 py-2 text-left">Owner</th>
                      <th className="px-2 py-2 text-right">Deals</th>
                      <th className="px-2 py-2 text-right">Total MRR</th>
                      <th className="px-2 py-2 text-right">Weighted MRR</th>
                      <th className="px-2 py-2 text-right">Commit MRR</th>
                      <th className="px-2 py-2 text-right">Expected MRR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {owners.map((o) => (
                      <tr key={o.owner} className="border-t border-slate-100" data-testid={`forecast-email-owner-row-${o.owner}`}>
                        <td className="px-2 py-1.5 font-medium text-slate-800">{o.owner}</td>
                        <td className="px-2 py-1.5 text-right text-slate-600 font-mono">{o.deals_count}</td>
                        <td className="px-2 py-1.5 text-right text-slate-900 font-mono">{fmtMoney(o.mrr)}</td>
                        <td className="px-2 py-1.5 text-right text-blue-700 font-mono font-semibold">{fmtMoney(o.weighted)}</td>
                        <td className="px-2 py-1.5 text-right">
                          <Input
                            type="number" step="0.01" min="0"
                            value={getField(o.owner, "commit")}
                            onChange={(e) => setField(o.owner, "commit", e.target.value)}
                            placeholder="£"
                            data-testid={`forecast-email-commit-${o.owner}`}
                            className="h-7 w-24 ml-auto text-right text-sm font-mono bg-amber-50 border-amber-200 focus:border-amber-400 focus:ring-amber-500/20"
                          />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <Input
                            type="number" step="0.01" min="0"
                            value={getField(o.owner, "expected")}
                            onChange={(e) => setField(o.owner, "expected", e.target.value)}
                            placeholder="£"
                            data-testid={`forecast-email-expected-${o.owner}`}
                            className="h-7 w-24 ml-auto text-right text-sm font-mono bg-emerald-50 border-emerald-200 focus:border-emerald-400 focus:ring-emerald-500/20"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={send}
            disabled={sending || !recipients.length}
            className="gap-1.5"
            data-testid="send-forecast-email-submit"
          >
            <Send className="h-4 w-4" />
            {sending ? "Sending…" : `Send to ${recipients.length || 0} recipient${recipients.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
